import React, { useState, useEffect } from 'react';
import { formatDate, formatDateTime } from '../lib/dateUtils';
import { motion, AnimatePresence } from 'motion/react';
import { Palette, Layers, Box, Type, Image as ImageIcon, Sparkles, Copy, FileUp, Zap, FileText, Layout, X as CloseIcon, FileIcon, RefreshCcw, GitBranch, Terminal, ShieldCheck, AlertCircle, Ship, Rocket, Edit3, X, Play, Smartphone, Tablet, Monitor, ChevronDown, ArrowLeft, Settings2, Github, Loader2 } from 'lucide-react';
import { generateDesignPrompt, validateSynthesis, chatWithGemini } from '../lib/gemini';
import { storage, ref, uploadBytes, getDownloadURL, db, auth, collection, addDoc, serverTimestamp, doc, setDoc, query, onSnapshot, orderBy, deleteDoc, where, getDocs, writeBatch, updateDoc, limit } from '../firebase';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { trackEvent } from '../lib/analytics';
import { useTasks } from '../contexts/TaskContext';
import { triggerSyntheticFeedback } from '../lib/feedback';
import { createCheckoutSession } from '../lib/stripe';
import { trackUserAction } from '../lib/events';
import { getPlanConfig, getUpgradeTrigger, UpgradeTriggerStore } from '../lib/pricing';
import { incrementGenerationUsage } from '../lib/usage';
import { UpgradeModal } from '../components/UpgradeModal';
import { listInstallationRepos } from '../lib/github-auth';
import { handleFirestoreError } from '../lib/firebaseUtils';

import { PaywallOverlay } from '../components/PaywallOverlay';

const TEMPLATES = [
  {
    id: 'ecommerce',
    title: 'E-commerce Marketplace',
    description: 'Multi-vendor marketplace with product discovery and checkout.',
    icon: Box,
    prompt: 'Design a modern multi-vendor e-commerce marketplace. Key components: Dynamic product catalog with advanced filtering, vendor storefronts, a secure multi-step checkout process, user reviews and ratings, and a seller dashboard for order management. Focus on high conversion and mobile-first responsiveness.'
  },
  {
    id: 'dev-tool',
    title: 'Developer SDK/Tool',
    description: 'Technical documentation, CLI reference, and integration guides.',
    icon: Terminal,
    prompt: 'Create a design brief for a new developer tool/SDK. Include: A landing page highlighting technical benefits, comprehensive API documentation structure, CLI command reference, interactive code playground examples, and a "Getting Started" guide that reduces friction for new developers.'
  },
  {
    id: 'fintech',
    title: 'Fintech Dashboard',
    description: 'Data-heavy analytics, transaction tracking, and security.',
    icon: ShieldCheck,
    prompt: 'Synthesize a fintech dashboard for personal wealth management. Requirements: Real-time transaction tracking, interactive data visualizations for spending habits, multi-account aggregation, security settings (2FA, biometric), and a clean, trustworthy aesthetic using a professional color palette.'
  },
  {
    id: 'social-community',
    title: 'Community Network',
    description: 'User-generated content, profiles, and engagement loops.',
    icon: Layers,
    prompt: 'Design a niche community social network. Features: Customizable user profiles, threaded discussions, real-time notifications, content discovery feed based on interests, and moderation tools. The UI should encourage engagement and feel welcoming to new members.'
  }
];

const DEVICE_PRESETS = [
  { id: 'iphone-se', name: 'iPhone SE', width: 375, height: 667, icon: Smartphone, category: 'Mobile' },
  { id: 'iphone-13', name: 'iPhone 13 / 14 / 15', width: 390, height: 844, icon: Smartphone, category: 'Mobile', default: true },
  { id: 'iphone-max', name: 'iPhone 13 Pro Max / 14 Plus / 15 Plus', width: 428, height: 926, icon: Smartphone, category: 'Mobile' },
  { id: 'android-sm', name: 'Small Android', width: 360, height: 640, icon: Smartphone, category: 'Mobile' },
  { id: 'android-std', name: 'Standard Android', width: 360, height: 800, icon: Smartphone, category: 'Mobile' },
  { id: 'android-lg', name: 'Large Android', width: 412, height: 915, icon: Smartphone, category: 'Mobile' },
  { id: 'ipad-mini', name: 'iPad Mini', width: 768, height: 1024, icon: Tablet, category: 'Tablet' },
  { id: 'ipad-std', name: 'iPad / iPad Air', width: 820, height: 1180, icon: Tablet, category: 'Tablet' },
  { id: 'ipad-pro-11', name: 'iPad Pro 11"', width: 834, height: 1194, icon: Tablet, category: 'Tablet' },
  { id: 'ipad-pro-12', name: 'iPad Pro 12.9"', width: 1024, height: 1366, icon: Tablet, category: 'Tablet' },
  { id: 'laptop-sm', name: 'Small Laptop', width: 1280, height: 800, icon: Monitor, category: 'Desktop' },
  { id: 'desktop-std', name: 'Standard Desktop', width: 1440, height: 900, icon: Monitor, category: 'Desktop' },
  { id: 'desktop-lg', name: 'Large Desktop', width: 1920, height: 1080, icon: Monitor, category: 'Desktop' },
  { id: 'responsive', name: 'Responsive', width: '100%', height: '100%', icon: Layout, category: 'Custom' },
];

export default function Synthesis({ profile, lastBuild, setLastBuild }: { profile: any, lastBuild: any, setLastBuild: any }) {
  const { tasks, startTask, updateTask, completeTask, failTask } = useTasks();
  const synthesisTask = tasks.synthesis;

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project');

  const [prompt, setPrompt] = useState('');
  const isGenerating = synthesisTask.status === 'running';
  const generationProgress = synthesisTask.progress;
  const [generationTime, setGenerationTime] = useState(0);
  const [targetOutput, setTargetOutput] = useState<'app' | 'landing'>('app');
  const [hasApiKey, setHasApiKey] = useState<boolean>(true);
  const [editingBuildId, setEditingBuildId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'code' | 'preview'>('preview');
  const [isValidating, setIsValidating] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [githubConnection, setGithubConnection] = useState<any>(null);
  const [isPushingToGithub, setIsPushingToGithub] = useState(false);

  const handleUpdateBuildMetadata = async (buildId: string) => {
    const toastId = toast.loading("Updating build metadata...");
    try {
      await updateDoc(doc(db, `projects/${projectId}/documents`, buildId), {
        title: editingTitle,
        description: editingDescription,
        updatedAt: serverTimestamp()
      });
      setEditingBuildId(null);
      toast.success("Build metadata updated", { id: toastId });
    } catch (err) {
      toast.error("Update failed", { id: toastId });
    }
  };
  const [buildCount, setBuildCount] = useState(0);
  const [previousBuilds, setPreviousBuilds] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(DEVICE_PRESETS.find(d => d.id === 'desktop-std') || DEVICE_PRESETS[11]);
  const [showDeviceMenu, setShowDeviceMenu] = useState(false);
  const [projectData, setProjectData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectId) return;
    const spec = searchParams.get('spec');
    if (spec) {
      setPrompt(spec);
      setTargetOutput('landing'); // Default to landing when coming from Accelerator
    }

    const unsubscribe = onSnapshot(doc(db, 'projects', projectId), (doc) => {
      if (doc.exists()) {
        setProjectData({ id: doc.id, ...doc.data() });
      }
    });

    const qGithub = query(
      collection(db, 'github_repo_connections'),
      where('projectId', '==', projectId),
      limit(1)
    );
    const unsubGithub = onSnapshot(qGithub, (snap) => {
      if (!snap.empty) {
        setGithubConnection(snap.docs[0].data());
      } else {
        setGithubConnection(null);
      }
    });

    return () => {
      unsubscribe();
      unsubGithub();
    };
  }, [projectId]);

  useEffect(() => {
    let timer: any;
    if (isGenerating) {
      timer = setInterval(() => {
        setGenerationTime(prev => prev + 1);
      }, 1000);
    } else {
      setGenerationTime(0);
    }
    return () => clearInterval(timer);
  }, [isGenerating]);

  const handleUpgrade = async () => {
    if (!auth.currentUser) {
      toast.error("Authentication required", {
        description: "Please sign in to upgrade your plan."
      });
      return;
    }

    const toastId = toast.loading("Preparing checkout...");
    try {
      await createCheckoutSession(auth.currentUser.uid, 'pro');
    } catch (error: any) {
      console.error("Upgrade error:", error);
      toast.error("Checkout Error", {
        id: toastId,
        description: error.message || "Failed to initiate checkout."
      });
    }
  };

  // Sync local state with lifted state
  const generatedBrief = lastBuild?.brief || null;
  const generatedFiles = lastBuild?.files || [];
  const lastGeneratedDocId = lastBuild?.docId || null;

  useEffect(() => {
    // If lastBuild changes and current module is not active, sync active file if needed
    if (lastBuild && !isGenerating) {
      // Logic handled via computed variables above
    }
  }, [lastBuild, isGenerating]);

  useEffect(() => {
    if (!projectId) return;
    const q = query(
      collection(db, `projects/${projectId}/documents`),
      orderBy('createdAt', 'desc'),
      limit(20) // Optimization: don't listen to everything, just recent history
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const builds = snap.docs.map(d => ({ id: d.id, ...d.data() as any }));
      setPreviousBuilds(builds);
      setBuildCount(builds.length);

      // Auto-select most recent or active build if none is selected
      if (builds.length > 0 && !lastBuild && !isGenerating) {
        // Favor the project's activeBuildId if it exists, otherwise use the most recent
        const targetBuild = builds.find(b => projectData?.activeBuildId === b.id) || builds[0];
        setLastBuild({
          brief: targetBuild.content,
          files: parseFiles(targetBuild.content),
          docId: targetBuild.id,
          title: targetBuild.title || targetBuild.id.slice(0, 8)
        });
      }
    });
    return unsubscribe;
  }, [projectId]);

  const handlePublish = async () => {
    if (!projectId || !generatedFiles.length || isPublishing) return;
    
    // Check deploy permission
    const trigger = getUpgradeTrigger(plan.id, usage, 'deploy');
    if (trigger) {
      setUpgradeTrigger(trigger);
      return;
    }

    setIsPublishing(true);
    const toastId = toast.loading(`Publishing build to Ship...`);

    try {
      // Create a unique deployment ID for this specific build
      const deploymentId = `${projectId}_build_${Date.now().toString().slice(-6)}`;
      const liveUrl = `${window.location.origin}/pub/${deploymentId}`;

      // Find index.html for the main preview content
      const indexFile = generatedFiles.find((f: any) => f.name.toLowerCase() === 'index.html') || generatedFiles[0];

      await setDoc(doc(db, 'deployments', deploymentId), {
        projectId,
        ownerId: auth.currentUser?.uid,
        ownerPlan: plan.id,
        documentId: lastGeneratedDocId || deploymentId, // Link to the source document if available
        title: lastBuild?.title || generatedFiles[0].name, // Use the generated title if available
        content: indexFile.content, // Main preview content
        brief: generatedBrief, // Keep the full brief for reference
        files: generatedFiles, // Save all files for the navigator
        status: 'Live',
        region: 'Global Edge',
        deployedAt: serverTimestamp(),
        url: liveUrl,
        liveUrl: liveUrl,
        type: 'micro-tool',
        folderId: null // Ensure it goes to Uncategorized
      });

      toast.success("Published to Ship", {
        description: `Your build is now live and available in the Ship engine.`,
        id: toastId
      });

      trackEvent('publish_micro_tool', { projectId, assetName: generatedFiles[0].name });
      
      // Navigate to Ship to see the new deployment
      navigate(`/studio/ship?project=${projectId}`);
    } catch (error: any) {
      console.error("Publish failed:", error);
      toast.error("Publish Failed", {
        description: error.message,
        id: toastId
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePushToGithub = async () => {
    if (!projectId || !generatedFiles.length || !githubConnection || isPushingToGithub) return;

    setIsPushingToGithub(true);
    const toastId = toast.loading(`Pushing build to GitHub: ${githubConnection.repoName}...`);

    try {
      const { token } = await auth.currentUser?.getIdTokenResult() || {};
      const res = await fetch('/api/github/repos/push', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
        },
        body: JSON.stringify({
          installationId: githubConnection.githubInstallationId,
          owner: githubConnection.repoOwner,
          repo: githubConnection.repoName,
          files: generatedFiles.map((f: any) => ({
            path: f.name,
            content: f.content
          })),
          commitMessage: `Synthesized build: ${lastBuild?.title || 'Auto-generated'}`,
          branch: githubConnection.branch || 'main'
        })
      });

      const data = await res.json();
      if (data.success) {
        toast.success("GitHub Synchronized", {
          description: "Latest synthesis files pushed to repository.",
          id: toastId
        });
        trackEvent('github_push_synthesis', { projectId, repo: githubConnection.repoName });
      } else {
        throw new Error(data.error || "GitHub push failed");
      }
    } catch (error: any) {
      console.error("GitHub sync failed:", error);
      toast.error("Sync Failed", {
        description: error.message,
        id: toastId
      });
    } finally {
      setIsPushingToGithub(false);
    }
  };

  const cleanContent = (text: string) => {
    let cleaned = text;
    // Remove <thought>...</thought> (non-greedy)
    cleaned = cleaned.replace(/<thought>[\s\S]*?<\/thought>/gi, '');
    
    // Remove various preamble/marker variants
    const markers = [
      /\[Project Architecture Initialized\]/gi,
      /Yacht Labs Synthesis Engine:?/gi,
      /Synthesizing architecture.../gi,
      /\[PROJECT ARCHITECTURE INITIALIZED\]/gi,
      /Micro-app synthesis initialized/gi
    ];
    
    markers.forEach(regex => {
      cleaned = cleaned.replace(regex, '');
    });

    cleaned = cleaned.trim();
    return cleaned;
  };

  const parseFiles = (text: string) => {
    const cleaned = cleanContent(text);
    const files: { name: string, content: string }[] = [];
    
    // Split by the [FILE: filename] marker
    const fileSegments = cleaned.split(/\[\s*FILE:\s*(.*?)\s*\]/gi);
    
    if (fileSegments.length <= 1) {
      // No delimiters found, treat the whole (cleaned) thing as one file
      const codeBlockMatch = cleaned.match(/```(?:html|css|javascript|js|typescript|ts|md)?\s*([\s\S]*?)```/i);
      let content = codeBlockMatch ? codeBlockMatch[1].trim() : cleaned;
      
      const guessName = isHtml('index.html', content) ? 'index.html' : 'README.md';
      files.push({ name: guessName, content });
    } else {
      // The first segment (index 0) is preamble/noise before the first [FILE:] occurs
      for (let i = 1; i < fileSegments.length; i += 2) {
        const name = (fileSegments[i] || '').trim().replace(/^[:\s]+|[:\s]+$/g, '');
        let content = fileSegments[i + 1] || '';
        
        // Strip markdown code blocks from the content segment
        if (content.includes('```')) {
          const firstTick = content.indexOf('```');
          const lastTick = content.lastIndexOf('```');
          
          if (firstTick !== -1 && lastTick !== -1 && firstTick !== lastTick) {
            const blockContent = content.slice(firstTick, lastTick + 3);
            const lines = blockContent.split('\n');
            if (lines[0].startsWith('```')) lines.shift();
            if (lines[lines.length - 1].startsWith('```')) lines.pop();
            content = lines.join('\n').trim();
          } else if (firstTick !== -1) {
            content = content.split('\n').filter(line => !line.trim().startsWith('```')).join('\n').trim();
          }
        }
        
        // Final sanity check: if the content still looks like it has a FILE tag, we might have nested or improper split
        content = content.replace(/\[\/?PROJECT ARCHITECTURE INITIALIZED\]/gi, '').trim();
        
        // Remove markdown section dividers (---) that AI sometimes injects inside files
        content = content.replace(/^---\s*$/gm, '').trim();
        
        if (name && content) {
          files.push({ name, content });
        }
      }
    }
    
    // If we have no files, use a safest fallback
    if (files.length === 0 && cleaned.trim().length > 0) {
      const content = cleaned.trim();
      const guessName = isHtml('index.html', content) ? 'index.html' : 'README.md';
      files.push({ name: guessName, content });
    }

    // Sort index.html to the top and filter out non-essential documentation/briefs
    return files
      .filter(f => !['README.md', 'BRIEF.md', 'ARCHITECTURE.md', 'NOTES.md'].includes(f.name.toUpperCase()))
      .sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        if (aName === 'index.html') return -1;
        if (bName === 'index.html') return 1;
        return aName.localeCompare(bName);
      });
  };

  const assembleHtml = (mainFile: any, allFiles: any[]) => {
    if (!mainFile?.name?.toLowerCase().endsWith('.html') && !mainFile?.content?.trim().toLowerCase().startsWith('<!doctype html>')) {
      return mainFile?.content || '';
    }

    let assembed = mainFile?.content || '';

    // Inline CSS
    allFiles.forEach(file => {
      if (file.name.endsWith('.css')) {
        const linkTag = new RegExp(`<link[^>]*href=["'](?:./)?${file.name}["'][^>]*>`, 'gi');
        if (linkTag.test(assembed)) {
          assembed = assembed.replace(linkTag, `<style>${file.content}</style>`);
        } else {
          assembed = assembed.replace('</head>', `<style>${file.content}</style></head>`);
        }
      }
    });

    // Inline JS
    allFiles.forEach(file => {
      if (file.name.endsWith('.js')) {
        const scriptTag = new RegExp(`<script[^>]*src=["'](?:./)?${file.name}["'][^>]*>\\s*</script>`, 'gi');
        if (scriptTag.test(assembed)) {
          assembed = assembed.replace(scriptTag, `<script>${file.content}</script>`);
        } else {
          assembed = assembed.replace('</body>', `<script>${file.content}</script></body>`);
        }
      }
    });

    return assembed;
  };

  const isHtml = (filename: string, content: string = '') => {
    if (filename.toLowerCase().endsWith('.html')) {
      // If extension says it's HTML, but it's just raw text (maybe a brief), 
      // check if it actually looks like HTML.
      if (content && !content.trim().startsWith('<') && !content.toLowerCase().includes('<!doctype') && !content.toLowerCase().includes('<html')) {
        // It might be a fake index.html fallback
        return false;
      }
      return true;
    }
    const trimmed = content.trim().toLowerCase();
    if (trimmed.startsWith('<!doctype html>') || trimmed.startsWith('<html') || trimmed.startsWith('<div') || trimmed.startsWith('<section')) return true;
    return false;
  };
  const isMarkdown = (filename: string) => filename.toLowerCase().endsWith('.md') || filename.toLowerCase().endsWith('.markdown');

  React.useEffect(() => {
    const checkKey = async () => {
      if ((window as any).aistudio) {
        const selected = await (window as any).aistudio.hasSelectedApiKey();
        setHasApiKey(selected);
      }
    };
    checkKey();
  }, []);

  const handleSelectKey = async () => {
    if ((window as any).aistudio) {
      await (window as any).aistudio.openSelectKey();
      setHasApiKey(true);
    }
  };



  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const [upgradeTrigger, setUpgradeTrigger] = useState<UpgradeTriggerStore>(null);

  const plan = getPlanConfig(profile?.plan);
  const usage = {
    generationsUsed: profile?.generationsUsed || 0,
    activeProjects: profile?.projectCount || 1,
    hasAttemptedDeploy: false
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Input Required", {
        description: "Please paste some notes to begin synthesis."
      });
      return;
    }
    if (!projectId) {
      toast.error("No Project Selected", {
        description: "Please select a project from the dashboard first."
      });
      return;
    }
    
    // Check pricing limits
    const trigger = getUpgradeTrigger(plan.id, usage, 'generate');
    if (trigger) {
      setUpgradeTrigger(trigger);
      return;
    }

    const controller = new AbortController();
    setAbortController(controller);
    startTask('synthesis', { projectId, targetOutput, prompt });
    setLastBuild(null);
    const toastId = toast.loading("Synthesizing your vision (running in background)...");
    
    const progressInterval = setInterval(() => {
      updateTask('synthesis', { 
        progress: Math.min(98, tasks.synthesis.progress + (tasks.synthesis.progress < 50 ? 5 : (tasks.synthesis.progress < 80 ? 2 : 0.5))) 
      });
    }, 500);
    
    try {
        // 1. Run AI generation
        let designPrompt = await generateDesignPrompt(targetOutput, prompt, [], projectData?.designConfig);
        
        if (controller.signal.aborted) return;

        if (designPrompt) {
          // Generate a unique title based on the content
          let generatedTitle = `${targetOutput.toUpperCase()} Design Build - ${new Date().toLocaleDateString()}`;
          try {
            const titleResponseText = await chatWithGemini([
              { role: "user", content: `Based on this design prompt, generate a short, unique, and descriptive title (max 5 words). Do NOT include quotes, special characters, markdown markers, or preamble. Return ONLY the title text. Prompt: ${prompt.slice(0, 500)}` }
            ], "You are a naming assistant. Return only a concise, professional title for the given project context.");
            if (titleResponseText) {
              generatedTitle = titleResponseText.trim()
                .replace(/^["']|["']$/g, '') 
                .replace(/\[.*?\]/g, '') 
                .replace(/```.*?```/gs, '') 
                .trim();
              
              if (!generatedTitle) {
                generatedTitle = `${targetOutput.toUpperCase()} Design Build - ${new Date().toLocaleDateString()}`;
              }
            }
          } catch (e) {
            console.error("Title generation failed:", e);
          }

          // 2. THE "VALIDATION" LAYER (INFERENCE)
          setIsValidating(true);
          const validation = await validateSynthesis(prompt, designPrompt);
          
          if (controller.signal.aborted) return;

          if (!validation.isValid) {
            console.log("Sanity check failed, re-synthesizing...", validation.reason);
            designPrompt = await generateDesignPrompt(targetOutput, prompt + "\n\nCRITICAL FIX: " + (validation.reason || "Ensure the output strictly follows the requested project goals."));
          }
          setIsValidating(false);
          updateTask('synthesis', { progress: 99 });

          if (controller.signal.aborted) return;

          const parsed = parseFiles(designPrompt);
          
          // 3. Save to Firestore as a new document
          const docRef = await addDoc(collection(db, `projects/${projectId}/documents`), {
            title: generatedTitle.slice(0, 50),
            content: designPrompt,
            projectId,
            ownerId: auth.currentUser?.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            folderId: null 
          });

        const buildResult = {
          brief: designPrompt,
          files: parsed,
          docId: docRef.id,
          title: generatedTitle.slice(0, 50)
        };

        setLastBuild(buildResult);
        setActiveFileIndex(0);

        // Set to 100% immediately when done
        clearInterval(progressInterval);
        completeTask('synthesis', buildResult);

        // Increment usage
        if (auth.currentUser) {
          incrementGenerationUsage(auth.currentUser.uid);
        }

        toast.success("Synthesis Complete", {
            description: `Your ${targetOutput} design build has been generated and saved.`,
            id: toastId
        });
        
        triggerSyntheticFeedback(projectId);
        trackEvent('synthesis_complete', { projectId, targetOutput });
        if (auth.currentUser) {
            trackUserAction(auth.currentUser.uid, 'synthesis_complete', `Synthesized ${targetOutput} build: ${generatedTitle}`, 'Synthesis', projectId);
        }

        // Reset local prompt form
        setPrompt('');
      } else {
        throw new Error("No prompt generated");
      }
    } catch (error: any) {
      clearInterval(progressInterval);
      if (error.name === 'AbortError') {
        failTask('synthesis', 'Generation cancelled');
      } else {
        console.error('Generation failed:', error);
        const userFriendlyMsg = handleFirestoreError(error, 'write', `projects/${projectId}/documents`);
        failTask('synthesis', userFriendlyMsg);
        toast.error("Synthesis Failed", { description: userFriendlyMsg, id: toastId });
      }
    } finally {
      clearInterval(progressInterval);
      setAbortController(null);
    }
  };

  const handleCancel = () => {
    if (abortController) {
      abortController.abort();
      toast.info("Build Cancelled");
    }
  };

  return (
    <div className="h-screen flex flex-col bg-zinc-950">
      <Toaster position="top-right" theme="dark" />
      <header className="p-6 border-b border-zinc-900 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link 
            to="/studio"
            className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 transition-all"
          >
            <ArrowLeft size={20} />
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center">
              <Layers className="text-zinc-100" size={20} />
            </div>
            <div>
              <h1 className="text-sm font-mono uppercase tracking-widest text-zinc-100">Build: The Assembler</h1>
              <p className="text-[10px] text-zinc-500 font-sans italic">Synthesize research into actionable design briefs.</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-mono uppercase tracking-widest transition-all ${
              showHistory ? 'bg-zinc-100 text-zinc-950' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-100'
            }`}
          >
            <GitBranch size={14} />
            Build History ({buildCount})
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Inputs */}
        <div className="w-full md:w-[400px] border-r border-zinc-900 flex flex-col bg-zinc-950 no-scrollbar shrink-0 relative">
          <AnimatePresence>
            {showHistory && (
              <motion.div 
                initial={{ x: -400 }}
                animate={{ x: 0 }}
                exit={{ x: -400 }}
                className="absolute inset-0 bg-[#050505] z-50 border-r border-[#222222] flex flex-col"
              >
                <div className="p-6 border-b border-[#222222] flex items-center justify-between">
                  <h2 className="tech-label text-white">Build History</h2>
                  <button onClick={() => setShowHistory(false)} className="text-[#888888] hover:text-white">
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-2 no-scrollbar sticky top-0">
                  {previousBuilds.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center p-8 space-y-4">
                      <GitBranch size={32} className="text-[#333333]" />
                      <p className="text-[10px] text-[#555555] font-mono uppercase tracking-widest">No builds found for this project.</p>
                    </div>
                  ) : (
                    previousBuilds.map((build) => (
                      <div
                        key={build.id}
                        onClick={() => {
                          setLastBuild({
                            brief: build.content,
                            files: parseFiles(build.content),
                            docId: build.id,
                            title: build.title
                          });
                          setShowHistory(false);
                          setActiveFileIndex(0);
                        }}
                        className={`w-full p-4 rounded-lg border text-left transition-all group relative cursor-pointer ${
                          lastGeneratedDocId === build.id 
                            ? 'bg-[#111111] border-[#0074D9] text-white' 
                            : 'bg-[#0A0A0A] border-[#222222] text-[#888888] hover:border-[#333333] hover:bg-[#111111]'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4 min-w-0">
                          <div className="group/item flex-1 min-w-0">
                            {editingBuildId === build.id ? (
                              <div className="space-y-3 p-2 bg-zinc-900/50 rounded-lg border border-zinc-800">
                                <input 
                                  value={editingTitle}
                                  onChange={(e) => setEditingTitle(e.target.value)}
                                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[11px] font-mono uppercase text-white focus:outline-none focus:border-[#0074D9]"
                                  autoFocus
                                />
                                <textarea 
                                  value={editingDescription}
                                  onChange={(e) => setEditingDescription(e.target.value)}
                                  placeholder="Build description..."
                                  className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-[9px] font-sans text-zinc-400 focus:outline-none focus:border-[#0074D9] min-h-[60px]"
                                />
                                <div className="flex gap-2">
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateBuildMetadata(build.id);
                                    }}
                                    className="px-3 py-1 bg-zinc-100 text-zinc-950 rounded text-[9px] font-mono uppercase font-bold"
                                  >
                                    Save
                                  </button>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingBuildId(null);
                                    }}
                                    className="px-3 py-1 bg-zinc-800 text-zinc-400 rounded text-[9px] font-mono uppercase"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-1 min-w-0">
                                <div className="flex items-center gap-2 group/title min-w-0">
                                  <h4 className="text-[11px] font-mono uppercase tracking-widest font-bold truncate pr-6 flex-1">{build.title}</h4>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingBuildId(build.id);
                                      setEditingTitle(build.title);
                                      setEditingDescription(build.description || "");
                                    }}
                                    className="opacity-0 group-hover/title:opacity-100 p-1 hover:text-white transition-all"
                                  >
                                    <Settings2 size={10} />
                                  </button>
                                </div>
                                {build.description && (
                                  <p className="text-[9px] text-zinc-500 font-sans line-clamp-2 italic">{build.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                  {projectData?.activeBuildId === build.id ? (
                                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-zinc-100 text-zinc-950 rounded-full text-[8px] font-mono uppercase tracking-widest font-bold">
                                      <Zap size={10} fill="currentColor" />
                                      Primary Build
                                    </div>
                                  ) : (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const toastId = toast.loading("Setting as primary build...");
                                        try {
                                          await updateDoc(doc(db, 'projects', projectId), {
                                            activeBuildId: build.id,
                                            updatedAt: serverTimestamp()
                                          });
                                          if (auth.currentUser) {
                                            trackUserAction(auth.currentUser.uid, 'primary_build_updated', `Set primary build to: ${build.title}`, 'Synthesis', projectId || undefined);
                                          }
                                          toast.success("Primary build updated", { id: toastId });
                                          trackEvent('set_active_build', { projectId, buildId: build.id });
                                        } catch (err) {
                                          toast.error("Update failed", { id: toastId });
                                        }
                                      }}
                                      className="px-2 py-0.5 border border-[#333333] hover:border-[#555555] hover:text-white rounded-full text-[8px] font-mono uppercase tracking-widest transition-all"
                                    >
                                      Set as Primary
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <button 
                            onClick={async (e) => {
                              e.stopPropagation();
                              const toastId = toast.loading("Deleting build...");
                              try {
                                // 1. Delete the source document
                                await deleteDoc(doc(db, `projects/${projectId}/documents`, build.id));
                                
                                // 2. Find and delete associated deployments in 'deployments' collection
                                const deploymentsQuery = query(
                                  collection(db, 'deployments'),
                                  where('documentId', '==', build.id)
                                );
                                const deploymentsSnap = await getDocs(deploymentsQuery);
                                const batch = writeBatch(db);
                                deploymentsSnap.docs.forEach(d => batch.delete(d.ref));
                                await batch.commit();

                                if (lastGeneratedDocId === build.id) setLastBuild(null);
                                toast.success("Build and deployments deleted", { id: toastId });
                              } catch (err) {
                                console.error("Delete failed:", err);
                                toast.error("Delete failed", { id: toastId });
                              }
                            }}
                            className={`p-2 rounded transition-all ${
                              lastGeneratedDocId === build.id
                                ? 'text-[#888888] hover:text-[#FF4444] hover:bg-[#FF4444]/10'
                                : 'text-[#555555] hover:text-[#FF4444] hover:bg-[#111111]'
                            }`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="flex-1 overflow-y-auto p-6 space-y-8 no-scrollbar">
            <div className="space-y-2">
              <h2 className="tech-label text-white">New Synthesis</h2>
              <p className="text-[10px] text-[#888888] font-sans leading-relaxed">Your synthesized briefs are automatically saved to the project editor.</p>
            </div>

            <div className="space-y-8">
              <section>
                <h3 className="tech-label text-[#555555] mb-4">Target Output</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'app', label: 'Application', icon: Box },
                    { id: 'landing', label: 'Landing Page', icon: Layout },
                  ].map((opt: any) => (
                    <button 
                      key={opt.id}
                      onClick={() => setTargetOutput(opt.id as any)}
                      className={`flex flex-col items-center justify-center gap-3 p-4 rounded-lg border transition-all relative ${
                        targetOutput === opt.id 
                          ? 'bg-[#111111] border-[#0074D9] text-white' 
                          : 'bg-[#050505] border-[#222222] text-[#555555] hover:text-[#888888] hover:border-[#333333]'
                      }`}
                    >
                      <opt.icon size={20} />
                      <span className="text-[9px] font-mono uppercase tracking-widest text-center">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="tech-label text-[#555555] mb-4">Template Library</h3>
                <div className="space-y-3">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setPrompt(template.prompt)}
                      className="tech-panel w-full flex items-start gap-4 p-4 hover:border-[#333333] hover:bg-[#0A0A0A] transition-all text-left group"
                    >
                      <div className="w-10 h-10 bg-[#111111] border border-[#222222] rounded-lg flex items-center justify-center text-[#555555] group-hover:text-[#0074D9] transition-colors shrink-0">
                        <template.icon size={18} />
                      </div>
                      <div>
                        <h4 className="text-[11px] font-mono uppercase tracking-widest text-white mb-1">{template.title}</h4>
                        <p className="text-[9px] text-[#888888] font-sans leading-relaxed line-clamp-2">{template.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </section>

              <section className="space-y-4">
                <h3 className="tech-label text-[#555555]">Raw Input & Research</h3>
                <textarea 
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Paste raw notes, data points, or project goals here..."
                  className="tech-input h-48 p-6 resize-none"
                />
              </section>

              {!hasApiKey ? (
                <div className="p-4 bg-[#FF851B]/10 border border-[#FF851B]/30 rounded-lg space-y-3">
                  <p className="text-[9px] text-[#FF851B] font-mono uppercase tracking-widest text-center">
                    Gemini API Key Required
                  </p>
                  <button 
                    onClick={handleSelectKey}
                    className="w-full py-3 bg-[#FF851B] text-black rounded font-mono uppercase text-[10px] font-bold tracking-widest hover:bg-[#FF851B]/90 transition-all"
                  >
                    Select API Key
                  </button>
                </div>
              ) : (
                <button 
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim()}
                  className="tech-button w-full py-5 flex items-center justify-center gap-3"
                >
                  {isGenerating ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      >
                        <Zap size={16} />
                      </motion.div>
                      Synthesizing...
                    </>
                  ) : (
                    <>
                      <Zap size={16} />
                      Build Product
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel: Output */}
        <div className="flex-1 bg-[#050505] relative overflow-hidden flex flex-col">
          {isGenerating ? (
            <div className="h-full flex flex-col items-center justify-center p-12 space-y-12">
              <div className="relative">
                <div className="w-32 h-32 border-4 border-[#222222] rounded-full" />
                <motion.div 
                  className="absolute inset-0 border-4 border-[#0074D9] rounded-full border-t-transparent"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Zap className="text-[#0074D9] animate-pulse" size={32} />
                </div>
              </div>
              
              <div className="w-full max-w-md space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="tech-label text-[#555555]">Build Progress</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono text-[#888888]">Elapsed: {generationTime}s</span>
                    <span className="text-[10px] font-mono text-white">{Math.round(generationProgress)}%</span>
                  </div>
                </div>
                <div className="w-full bg-[#111111] rounded-full h-2 overflow-hidden border border-[#222222]">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${generationProgress}%` }}
                    className="bg-[#0074D9] h-full shadow-[0_0_15px_rgba(0,116,217,0.5)]"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center space-y-2">
                    <div className={`h-1 rounded-full ${generationProgress > 30 ? 'bg-[#0074D9]' : 'bg-[#222222]'}`} />
                    <span className="text-[8px] font-mono uppercase text-[#555555]">Architect</span>
                  </div>
                  <div className="text-center space-y-2">
                    <div className={`h-1 rounded-full ${generationProgress > 60 ? 'bg-[#0074D9]' : 'bg-[#222222]'}`} />
                    <span className="text-[8px] font-mono uppercase text-[#555555]">Synthesize</span>
                  </div>
                  <div className="text-center space-y-2">
                    <div className={`h-1 rounded-full ${generationProgress > 90 ? 'bg-[#0074D9]' : 'bg-[#222222]'}`} />
                    <span className="text-[8px] font-mono uppercase text-[#555555]">Validate</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleCancel}
                className="tech-button-secondary text-[#555555] hover:bg-[#FF4444]/10 hover:text-[#FF4444] hover:border-[#FF4444]/50"
              >
                <X size={14} /> Cancel Build
              </button>
            </div>
          ) : generatedBrief ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="h-full flex flex-col overflow-hidden"
            >
              <div className="flex-1 overflow-y-auto no-scrollbar p-6 md:p-8">
                <div className="bg-[#0A0A0A] border border-[#222222] rounded-xl flex flex-col h-full min-h-[800px]">
                  <div className="p-8 border-b border-[#222222] flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Terminal size={14} className="text-[#0074D9]" />
                        <h2 className="tech-label text-white">Synthesis Workbench</h2>
                      </div>
                      <p className="text-[10px] text-[#555555] uppercase tracking-widest">Multi-Dimensional Project Architecture for {targetOutput}</p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex items-center bg-[#111111] rounded-lg p-1 border border-[#222222]">
                        <button 
                          onClick={() => setViewMode('preview')}
                          className={`px-3 py-1.5 rounded text-[10px] font-mono uppercase tracking-widest transition-all ${
                            viewMode === 'preview' ? 'bg-[#222222] text-white' : 'text-[#888888] hover:text-[#CCCCCC]'
                          }`}
                        >
                          Preview
                        </button>
                        <button 
                          onClick={() => setViewMode('code')}
                          className={`px-3 py-1.5 rounded text-[10px] font-mono uppercase tracking-widest transition-all ${
                            viewMode === 'code' ? 'bg-[#222222] text-white' : 'text-[#888888] hover:text-[#CCCCCC]'
                          }`}
                        >
                          Code
                        </button>
                      </div>

                      <div className="h-8 w-px bg-[#222222] mx-1" />

                      <div className="flex items-center gap-2">
                        {githubConnection && (
                          <button 
                            onClick={handlePushToGithub}
                            disabled={isPushingToGithub}
                            title={`Sync to ${githubConnection.repoName}`}
                            className="bg-[#111111] border border-[#222222] text-[#888888] hover:text-[#0074D9] hover:border-[#0074D9]/50 p-2 rounded-lg transition-all disabled:opacity-50"
                          >
                            {isPushingToGithub ? <Loader2 size={16} className="animate-spin" /> : <Github size={16} />}
                          </button>
                        )}
                        <button 
                          onClick={handlePublish}
                          disabled={isPublishing}
                          className="tech-button flex items-center gap-2 px-4 py-2"
                        >
                          {isPublishing ? <RefreshCcw size={14} className="animate-spin" /> : <Ship size={14} />}
                          Publish to Ship
                        </button>
                      </div>
                      <div className="h-8 w-px bg-[#222222] mx-1" />
                      <button 
                        onClick={() => setLastBuild(null)}
                        className="p-2 bg-[#111111] hover:bg-[#1A1A1A] text-[#555555] hover:text-white rounded transition-colors"
                      >
                        <CloseIcon size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Asset Toggle (Tabs) */}
                  <div className="px-8 flex items-center justify-between border-b border-[#222222] bg-[#050505]">
                    <div className="flex items-center gap-2 overflow-x-auto py-4 no-scrollbar max-w-[70%]">
                      {generatedFiles.map((file: any, idx: number) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setActiveFileIndex(idx);
                          }}
                          className={`flex items-center gap-2 px-4 py-2 rounded text-[10px] font-mono uppercase tracking-widest transition-all whitespace-nowrap ${
                            activeFileIndex === idx 
                              ? 'bg-[#0074D9] text-white shadow-[0_0_20px_rgba(0,116,217,0.3)]' 
                              : 'bg-[#111111] text-[#555555] hover:text-[#888888]'
                          }`}
                        >
                          <FileIcon size={12} />
                          {file.name}
                        </button>
                      ))}
                    </div>

                    {viewMode === 'preview' && (
                      <div className="relative py-4">
                        <button 
                          onClick={() => setShowDeviceMenu(!showDeviceMenu)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#111111] border border-[#222222] rounded text-[10px] font-mono uppercase tracking-widest text-[#888888] hover:text-white transition-all"
                        >
                          <selectedDevice.icon size={12} />
                          {selectedDevice.name}
                          <ChevronDown size={12} className={`transition-transform ${showDeviceMenu ? 'rotate-180' : ''}`} />
                        </button>

                        <AnimatePresence>
                          {showDeviceMenu && (
                            <motion.div 
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: 10 }}
                              className="absolute top-full right-0 mt-2 w-64 bg-[#0A0A0A] border border-[#222222] rounded shadow-2xl z-50 overflow-hidden"
                            >
                              <div className="p-3 border-b border-[#222222]">
                                <span className="text-[8px] font-mono uppercase tracking-widest text-[#555555]">Device Presets</span>
                              </div>
                              <div className="max-h-64 overflow-y-auto no-scrollbar">
                                {DEVICE_PRESETS.map((device) => (
                                  <button
                                    key={device.id}
                                    onClick={() => {
                                      setSelectedDevice(device);
                                      setShowDeviceMenu(false);
                                    }}
                                    className={`w-full flex items-center gap-3 p-3 hover:bg-[#111111] transition-all text-left ${selectedDevice.id === device.id ? 'bg-[#111111]' : ''}`}
                                  >
                                    <device.icon size={14} className={selectedDevice.id === device.id ? 'text-[#0074D9]' : 'text-[#555555]'} />
                                    <div>
                                      <p className="text-[10px] font-mono uppercase text-white">{device.name}</p>
                                      <p className="text-[8px] text-[#555555]">{device.width} x {device.height}</p>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>

                  {/* Workbench Output Area */}
                  <div className="flex-1 relative group overflow-hidden">
                    <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(generatedFiles[activeFileIndex]?.content || '');
                          toast.success("Copied to clipboard");
                        }}
                        className="p-3 bg-[#111111]/80 backdrop-blur-sm border border-[#222222] text-white rounded hover:bg-[#1A1A1A] transition-all shadow-xl"
                        title="Copy asset content"
                      >
                        <Copy size={16} />
                      </button>
                    </div>

                    <div 
                      className={`synthesis-output h-full max-w-none bg-[#050505] font-sans text-sm leading-relaxed transition-all duration-300 ${
                        viewMode === 'code' ? 'p-8 overflow-y-auto' : ''
                      }`}
                    >
                      {viewMode === 'code' ? (
                        <pre className="text-[#CCCCCC] font-mono whitespace-pre-wrap text-xs leading-relaxed">
                          {generatedFiles[activeFileIndex]?.content || ''}
                        </pre>
                      ) : (
                        <div className="relative w-full h-full flex flex-col items-center bg-[#111111]/50 overflow-auto p-8 no-scrollbar">
                          <div 
                            style={{ 
                              width: selectedDevice.width === '100%' ? '100%' : `${selectedDevice.width}px`,
                              maxWidth: '100%',
                              height: selectedDevice.height === '100%' ? 'auto' : `${selectedDevice.height}px`,
                              minHeight: selectedDevice.height === '100%' ? '600px' : 'auto'
                            }}
                            className="bg-white transition-all duration-300 overflow-hidden shadow-2xl rounded shrink-0"
                          >
                            {isHtml(generatedFiles[activeFileIndex]?.name || '', generatedFiles[activeFileIndex]?.content || '') ? (
                              <iframe
                                srcDoc={assembleHtml(generatedFiles[activeFileIndex], generatedFiles)}
                                className="w-full h-full border-none"
                                title="Prototype Preview"
                                sandbox="allow-scripts allow-forms allow-popups allow-modals allow-pointer-lock allow-same-origin"
                                onLoad={(e) => {
                                  const iframe = e.target as HTMLIFrameElement;
                                  iframe.contentWindow?.focus();
                                }}
                              />
                            ) : (
                              <div className="p-8 md:p-12 bg-[#050505] h-full overflow-y-auto prose prose-invert prose-zinc max-w-none no-scrollbar">
                                <Markdown remarkPlugins={[remarkGfm]}>
                                  {generatedFiles[activeFileIndex]?.content || ''}
                                </Markdown>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Validation Status Footer */}
                  <div className="p-6 bg-[#050505] border-t border-[#222222] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#2ECC40]/10 rounded flex items-center justify-center">
                        <ShieldCheck size={16} className="text-[#2ECC40]" />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono uppercase text-white">Sanity Check Passed</p>
                        <p className="text-[9px] text-[#888888] font-sans">Build verified against original mission goals.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-[#2ECC40] rounded-full animate-pulse" />
                      <span className="text-[9px] font-mono uppercase text-[#555555] tracking-widest">Robust Build v1.0</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-12 text-center space-y-6">
              <div className="w-20 h-20 bg-[#111111] rounded-2xl flex items-center justify-center text-[#333333]">
                <Box size={40} />
              </div>
              <div className="space-y-2">
                <h3 className="tech-label text-[#555555]">No Active Build</h3>
                <p className="text-[10px] text-[#555555] max-w-xs mx-auto uppercase tracking-widest leading-relaxed">
                  Configure your project goals on the left to synthesize a new project architecture.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
